import { createFileRoute, Link } from '@tanstack/react-router'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTranslation } from 'react-i18next'
import { useLang } from '@/hooks/useLang'
import { COUNTRIES_CONFIG, CountryConfig } from '@/config/countries'
import { countryToSlug, getCountryName } from '@/lib/countries'
import { Globe, Search, ArrowRight, Phone } from 'lucide-react'
import { Flag } from '@/components/Flag'
import { generateSeo } from '@/lib/seo'
import { Breadcrumbs } from '@/components/Breadcrumbs'
import { ToolPageHero } from '@/components/ToolPageHero'
import { FAQSection } from '@/components/FAQSection'
import { CTASection } from '@/components/CTASection'
import { useMemo, useState } from 'react'

export const Route = createFileRoute('/$lang/tools/country-codes')({
  staticData: {
    sitemap: { priority: '0.8', changefreq: 'monthly' },
  },
  component: CountryCodesToolPage,
  head: ({ match }) => {
    const { meta, links } = generateSeo({
      title: 'International Country Calling Codes - Complete List | PhoneSalad',
      description: 'Find the international dialing code for any country. Search our complete list of 200+ country phone codes instantly.',
      pathname: match.pathname,
      keywords: 'country calling codes, international dialing codes, phone country codes, country code lookup, ISD codes',
    })
    return { meta, links }
  },
})

// Group countries by region
const countriesByRegion = Object.values(COUNTRIES_CONFIG).reduce(
  (acc, country) => {
    const region = country.region
    if (!acc[region]) {
      acc[region] = []
    }
    acc[region].push(country)
    return acc
  },
  {} as Record<string, CountryConfig[]>
)

// Sort regions alphabetically
const sortedRegions = Object.keys(countriesByRegion).sort()

// Sort countries within each region alphabetically by English name
sortedRegions.forEach(region => {
  countriesByRegion[region].sort((a, b) => getCountryName(a.code).localeCompare(getCountryName(b.code)))
})

function CountryCodesToolPage() {
  const { t } = useTranslation(['tools', 'common'])
  const lang = useLang()
  const [searchQuery, setSearchQuery] = useState('')

  // Filter countries based on search query (searches translated name)
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return null

    const query = searchQuery.toLowerCase().trim()
    const allCountries = Object.values(COUNTRIES_CONFIG)

    return allCountries.filter((country) => {
      const translatedName = t(`countries.${country.code}`)
      const nameMatch = translatedName.toLowerCase().includes(query)
      const codeMatch = country.code.toLowerCase().includes(query)
      const dialingCodeMatch = country.dialingCode.toLowerCase().includes(query)
      return nameMatch || codeMatch || dialingCodeMatch
    })
  }, [searchQuery, t])

  const faqItems = t('tools.countryCodes.faq.items', { returnObjects: true }) as unknown as Array<{ question: string; answer: string }>

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('tools.breadcrumb'),
            to: '/$lang/tools',
          },
          {
            label: t('tools.countryCodes.title'),
          },
        ]}
      />

      <ToolPageHero
        icon={Globe}
        badgeText={t('tools.countryCodes.title')}
        title={t('tools.countryCodes.title')}
        description={t('tools.countryCodes.description')}
      >
        {/* Search Box */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('tools.countryCodes.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 text-lg"
              />
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              {t('tools.countryCodes.searchDescription')}
            </p>
          </CardContent>
        </Card>
      </ToolPageHero>

      {/* Results */}
      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-6xl">
          {filteredCountries ? (
            // Search results
            filteredCountries.length > 0 ? (
              <>
                <h2 className="text-2xl font-bold mb-6">
                  {t('common.search')}: "{searchQuery}"
                </h2>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {filteredCountries.map((country) => (
                    <CountryCard
                      key={country.code}
                      country={country}
                      lang={lang}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {t('tools.countryCodes.noResults')}
                </p>
              </div>
            )
          ) : (
            // Grouped by region
            <div className="space-y-12">
              {sortedRegions.map((region) => (
                <div key={region}>
                  <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary"></span>
                    {t(`tools.countryCodes.regions.${region}`, { defaultValue: region })}
                  </h2>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {countriesByRegion[region].map((country) => (
                      <CountryCard
                        key={country.code}
                        country={country}
                        lang={lang}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <FAQSection
        title={t('tools.countryCodes.faq.title')}
        items={faqItems}
      />

      <CTASection
        translationKeyRoot="tools.countryCodes.cta" ns="tools"
        buttonTo="/dialer"
        buttonIcon={Phone}
      />
    </>
  )
}

interface CountryCardProps {
  country: CountryConfig
  lang: string
}

function CountryCard({ country, lang }: CountryCardProps) {
  const { t } = useTranslation(['tools', 'common'])
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Flag countryCode={country.code} size="xl" />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground truncate">
              {t(`countries.${country.code}`)}
            </h3>
            <div className="text-sm text-muted-foreground space-y-1 mt-1">
              <div className="flex justify-between">
                <span>{t('tools.countryCodes.columns.isoCode')}:</span>
                <span className="font-mono">{country.code}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('tools.countryCodes.columns.dialingCode')}:</span>
                <span className="font-mono font-semibold text-green-600">{country.dialingCode}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4 pt-3 border-t">
          <Button variant="outline" size="sm" className="flex-1" asChild>
            <a
              href={`/dialer?phone=${encodeURIComponent(country.dialingCode)}`}
              className="flex items-center justify-center gap-1"
            >
              <Phone className="w-3 h-3" />
              {t('tools.countryCodes.callNow')}
            </a>
          </Button>
          <Button variant="ghost" size="sm" className="flex-1" asChild>
            <Link
              to="/$lang/rates/$country"
              params={{ lang, country: countryToSlug(country.code) }}
              className="flex items-center justify-center gap-1"
            >
              {t('tools.countryCodes.viewRates')}
              <ArrowRight className="w-3 h-3" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
